import type {
  InterventionSectionDatasetLoadingProps,
  InterventionSectionDatasetLoadingStatusStep,
} from "./intervention-section-datasets.type";
import type {
  Dataset,
  DatasetCartesian,
  DatasetFile,
  DatasetForm,
  DatasetGps,
  DatasetImage,
  DatasetPlanPositioning,
  DatasetType,
} from "@/entities/dataset";
import type {
  SchemaDataset,
  SchemaDatasetCartesian,
  SchemaDatasetFile,
  SchemaDatasetForm,
  SchemaDatasetGps,
  SchemaDatasetImage,
  SchemaDatasetPlanPositioning,
} from "@/entities/schema";

import { useEffect, useState } from "react";

import {
  DATASET_TYPE,
  groupDatasetsByType,
  sortDatasetsByDate,
} from "@/entities/dataset";
import {
  getInterventionTemplateChildren,
  groupTemplatesByType,
} from "@/entities/intervention-template";
import { useDatasetsMutatorDatasetTypeStatus } from "@/modules/datasets/datasets-mutator";
import { useInterventionSectionRenderIndex } from "@/modules/intervention/intervention-section";
import { useInterventionSectionContext } from "@/modules/intervention/intervention-section-context";
import { getObjectId } from "@/utilities/object";

export const useInterventionSectionDatasets = () => {
  const { sectionTemplate, sectionDatasets } = useInterventionSectionContext();

  const templates = getInterventionTemplateChildren(sectionTemplate);
  const templatesByType = groupTemplatesByType(templates);
  const datasetsByType = groupDatasetsByType(sectionDatasets);

  const hasTemplate = (type: DatasetType): boolean => {
    return (templatesByType[type]?.length ?? 0) > 0;
  };

  const hasDatasets = (type: DatasetType): boolean => {
    return (datasetsByType[type]?.length ?? 0) > 0;
  };

  return {
    sectionTemplate,
    templates,
    templatesByType,
    hasTemplate,
    sectionDatasets,
    datasetsByType,
    hasDatasets,
  };
};

export const useInterventionSectionDataset = <
  D extends Dataset,
  T extends SchemaDataset,
>(
  type: DatasetType
) => {
  const { isEditable } = useInterventionSectionContext();
  const { templatesByType, datasetsByType } = useInterventionSectionDatasets();

  const { isSaving, toBeDeleted } = useDatasetsMutatorDatasetTypeStatus(type);
  const { isLoading } = useInterventionSectionDatasetLoading({ isSaving });

  const loading = isLoading || isSaving;
  const disabled = !isEditable;

  // Make sure `template` & `dataset` related typing contains `undefined`
  const templates = (templatesByType[type] ?? []) as T[];
  const template = templates[0] ? templates[0] : undefined;
  const datasets = (datasetsByType[type] ?? []) as D[];
  const dataset = datasets[0] ? datasets[0] : undefined;

  // Create a dynamic key based on `renderIndex` to make sure each dataset
  // is re-mounted on save bar cancel click and internal state is resetted.
  const { renderIndex } = useInterventionSectionRenderIndex();
  const datasetId = getObjectId(dataset);
  const id = datasetId ?? template?.layout_index ?? "null";
  const key = `dataset_${type}_${id}_${renderIndex}`;

  return {
    key,
    template,
    datasets,
    dataset,
    loading,
    disabled,
    toBeDeleted,
  };
};

export const useInterventionSectionDatasetLoading = ({
  isSaving,
}: InterventionSectionDatasetLoadingProps) => {
  const { observationQuery } = useInterventionSectionContext();
  const { isFetching, isLoading } = observationQuery;

  const [loadingStatus, setLoadingStatus] =
    useState<InterventionSectionDatasetLoadingStatusStep>("idle");

  useEffect(() => {
    const shouldGoToStep1 = isSaving && loadingStatus === "idle";
    if (shouldGoToStep1) setLoadingStatus("1-saving");

    const shouldGoToStep2 = !isSaving && loadingStatus === "1-saving";
    if (shouldGoToStep2) setLoadingStatus("2-before-fetch");
  }, [isSaving, loadingStatus]);

  useEffect(() => {
    const shouldGoToStep3 = isFetching && loadingStatus === "2-before-fetch";
    if (shouldGoToStep3) setLoadingStatus("3-fetching");

    const shouldReset = !isFetching && loadingStatus === "3-fetching";
    if (shouldReset) setLoadingStatus("idle");
  }, [isFetching, loadingStatus]);

  return {
    loadingStatus,
    isLoading: isLoading || loadingStatus !== "idle",
  };
};

export const useInterventionSectionDatasetForm = () => {
  return useInterventionSectionDataset<DatasetForm, SchemaDatasetForm>(
    DATASET_TYPE.FORM
  );
};

export const useInterventionSectionDatasetImage = () => {
  const result = useInterventionSectionDataset<
    DatasetImage,
    SchemaDatasetImage
  >(DATASET_TYPE.IMAGE);

  const sortedDatasetsById = [...result.datasets].sort((a, b) => a.id - b.id);

  const sortedDatasetsByDate = sortDatasetsByDate<DatasetImage>({
    datasets: sortedDatasetsById,
    property: "created_at",
    order: "asc",
  });

  return {
    ...result,
    datasets: sortedDatasetsByDate,
    dataset: sortedDatasetsByDate[0] || undefined,
  };
};

export const useInterventionSectionDatasetGps = () => {
  return useInterventionSectionDataset<DatasetGps, SchemaDatasetGps>(
    DATASET_TYPE.GPS
  );
};

export const useInterventionSectionDatasetFile = () => {
  return useInterventionSectionDataset<DatasetFile, SchemaDatasetFile>(
    DATASET_TYPE.FILE
  );
};

export const useInterventionSectionDatasetCartesian = () => {
  return useInterventionSectionDataset<
    DatasetCartesian,
    SchemaDatasetCartesian
  >(DATASET_TYPE.CARTESIAN);
};

export const useInterventionSectionDatasetPlanPositioning = () => {
  return useInterventionSectionDataset<
    DatasetPlanPositioning,
    SchemaDatasetPlanPositioning
  >(DATASET_TYPE.PLAN_POSITIONING);
};
